import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { applicationsApi, jobsApi, ApiError } from "@/api";
import type {
  ReviewQueueResponse,
  ReviewUnmatchedApp,
  ReviewAppSummary,
  ReviewDuplicateGroup,
  ReviewMatchConfidence,
  ApplicationMergePreview,
} from "@/types";
import { Button, Pill, Spinner, Toast, useToast } from "@/components/ui";
import type { StatusTone } from "@/lib/status";
import { humanise } from "@/lib/jobs";
import { formatDateDMY } from "@/lib/format";
import { JobPicker } from "./JobPicker";
import "./review.css";

/* ─── display helpers ──────────────────────────────────────────────────── */

function appTypeLabel(type: string | null): string {
  if (type === "building_control") return "Building control";
  if (type === "planning") return "Planning";
  return humanise(type);
}

/** Tone for the match_status pill. Pills have no warning tone, so needs_review
 *  reads as the soft-info "attention" grey; ref collisions are the real danger. */
function matchStatusTone(ms: string): StatusTone {
  if (ms.includes("collision")) return "danger";
  if (ms === "matched") return "success";
  if (ms === "auto_created") return "info";
  if (ms === "needs_review") return "info-soft";
  return "neutral";
}

function confidenceLabel(c: ReviewMatchConfidence): string {
  return c === "address_match" ? "Address match" : "Postcode only";
}

function refOf(app: ReviewAppSummary): string {
  return app.app_reference || `application ${app.id}`;
}

/** Minimal job descriptor, fetched lazily. Suggestions only carry a job *id*, and
 *  the duplicate record's own reference is identical to the orphan's — so naming
 *  the job by number + address is the only way to make a suggestion meaningful. */
type JobInfo = { number: number; name: string };

/** Human label for a job id using the fetched map. Auto-created shell jobs carry
 *  `job_number = 0` but DO have a name/address — prefer the name there rather than
 *  the raw id. `number === -1` is the lookup-failed sentinel; `undefined` = still
 *  loading. */
function jobLabelText(jobInfo: Record<number, JobInfo>, id: number): string {
  const i = jobInfo[id];
  if (!i) return "…";
  if (i.number > 0) return i.name ? `#${i.number} · ${i.name}` : `#${i.number}`;
  if (i.number === 0 && i.name) return i.name; // auto-created shell job (no number yet)
  return `job ${id}`; // lookup failed, or a number-0 job with no name at all
}

/** True when the target is an auto-created shell job (number 0) — likely a
 *  duplicate of a real job; flag it so the user doesn't treat it as canonical. */
function isAutoJob(jobInfo: Record<number, JobInfo>, id: number): boolean {
  return jobInfo[id]?.number === 0;
}

/** Short job label for the success toast. */
function jobToastDesc(jobInfo: Record<number, JobInfo>, id: number): string {
  const i = jobInfo[id];
  if (i && i.number > 0) return `job #${i.number}`;
  if (i && i.number === 0 && i.name) return `the auto-created job for ${i.name}`;
  return `job ${id}`;
}

/** Caveat pill for an auto-created (number-0) target job. */
function AutoJobPill() {
  return (
    <Pill
      tone="neutral"
      title="Auto-created job with no job number — likely a duplicate of a real job. Merging duplicate jobs is Phase 1b; to attach to the real job instead, use “Find a job…” to search for it."
    >
      Auto-created
    </Pill>
  );
}

/** Whether (and how) a duplicate group can be merged, mirroring the endpoint's
 *  server-side preconditions so the button states match what /merge will accept. */
type MergePlan =
  | { ok: true; allowOrphan: boolean }
  | { ok: false; reason: string };

function planMerge(apps: ReviewAppSummary[]): MergePlan {
  if (apps.length < 2) return { ok: false, reason: "Need at least 2 records to merge." };
  if (apps.length > 10) return { ok: false, reason: "Too many records to merge at once (max 10)." };
  if (new Set(apps.map((a) => a.type)).size > 1)
    return { ok: false, reason: "Mixed planning + building control — merge each type separately." };

  const withJob = apps.filter((a) => a.job_id > 0);
  const withoutJob = apps.filter((a) => a.job_id === 0);
  const distinctJobs = new Set(withJob.map((a) => a.job_id));

  // Standard: every record on the same (non-zero) job.
  if (withoutJob.length === 0 && distinctJobs.size === 1) return { ok: true, allowOrphan: false };
  // Orphan-loser: exactly one on a real job, the rest still unlinked.
  if (withJob.length === 1 && withoutJob.length >= 1) return { ok: true, allowOrphan: true };
  if (distinctJobs.size > 1) return { ok: false, reason: "Records are linked to different jobs — resolve that first." };
  return { ok: false, reason: "Link a record to a job first, then merge." };
}

/* ─── screen ───────────────────────────────────────────────────────────── */

/** Review & Merge triage (Phase 1). Surfaces the reconciliation queue —
 *  unmatched applications (with suggested jobs / duplicate siblings) and
 *  duplicate groups — and links an orphan to a job (the one live write). Record
 *  merge / dismiss are Phase 1b and shown disabled. */
export function ReviewQueue() {
  const [data, setData] = useState<ReviewQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [jobInfo, setJobInfo] = useState<Record<number, JobInfo>>({});
  const [mergeTarget, setMergeTarget] = useState<{
    apps: ReviewAppSummary[];
    allowOrphan: boolean;
    groupKey: string;
  } | null>(null);
  const { toast, showToast } = useToast();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    applicationsApi
      .getReviewQueue()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load the review queue.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  // Every distinct job a suggestion / duplicate member points at (by id only).
  const jobIds = useMemo(() => {
    const ids = new Set<number>();
    for (const app of data?.unmatched_applications ?? [])
      for (const sib of app.suggested_apps) if (sib.job_id > 0) ids.add(sib.job_id);
    for (const g of data?.duplicate_applications ?? [])
      for (const a of g.applications) if (a.job_id > 0) ids.add(a.job_id);
    for (const g of data?.possible_duplicate_applications ?? [])
      for (const a of g.applications) if (a.job_id > 0) ids.add(a.job_id);
    return [...ids];
  }, [data]);

  // Fetch each referenced job once so suggestions can name it. A failed lookup is
  // cached as a sentinel (number 0) so it isn't retried forever.
  useEffect(() => {
    const missing = jobIds.filter((id) => !(id in jobInfo));
    if (missing.length === 0) return;
    let cancelled = false;
    Promise.all(
      missing.map((id) =>
        jobsApi
          .getJobDetail(id)
          .then((r) => ({
            id,
            info: { number: r.job.job_number, name: r.job.job_name || r.job.full_address || "" } as JobInfo,
          }))
          .catch(() => ({ id, info: { number: -1, name: "" } as JobInfo })),
      ),
    ).then((rows) => {
      if (cancelled) return;
      setJobInfo((prev) => {
        const next = { ...prev };
        for (const { id, info } of rows) next[id] = info;
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [jobIds, jobInfo]);

  async function link(app: ReviewAppSummary, jobId: number, jobDesc: string) {
    if (busyId != null) return;
    setBusyId(app.id);
    try {
      await applicationsApi.linkAppToJob(app.id, jobId);
      showToast(`Linked ${refOf(app)} → ${jobDesc}`);
      setReloadKey((k) => k + 1); // the app drops out of the queue on reload
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not link the application.", "warn");
    } finally {
      setBusyId(null);
    }
  }

  const counts = data?.counts;
  const unmatched = data?.unmatched_applications ?? [];
  const duplicates = data?.duplicate_applications ?? [];
  const possibleDuplicates = data?.possible_duplicate_applications ?? [];

  return (
    <section className="review">
      <header className="review__head">
        <div>
          <h1>Review</h1>
          {counts ? (
            <p className="review__count">
              {counts.unmatched} unmatched · {counts.duplicate_groups} duplicate group{counts.duplicate_groups === 1 ? "" : "s"} ·{" "}
              {counts.possible_duplicate_groups} possible
            </p>
          ) : null}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setReloadKey((k) => k + 1)} disabled={loading}>
          Refresh
        </Button>
      </header>

      {error ? (
        <p className="leads__error review__error" role="alert">
          {error}
          <button type="button" className="review__retry" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </button>
        </p>
      ) : null}

      {loading ? (
        <div className="review__loading">
          <Spinner label="Loading review queue…" />
        </div>
      ) : data ? (
        unmatched.length === 0 && duplicates.length === 0 && possibleDuplicates.length === 0 ? (
          <p className="review__empty">Nothing to review — every application is matched to a job.</p>
        ) : (
          <>
            {unmatched.length > 0 ? (
              <div className="review__section">
                <h2 className="review__section-title">Unmatched applications ({unmatched.length})</h2>
                {unmatched.map((app) => (
                  <UnmatchedCard
                    key={app.id}
                    app={app}
                    busy={busyId === app.id}
                    jobInfo={jobInfo}
                    onLink={(jobId, desc) => link(app, jobId, desc)}
                  />
                ))}
              </div>
            ) : null}

            {duplicates.length > 0 ? (
              <div className="review__section">
                <h2 className="review__section-title">Duplicate references ({duplicates.length})</h2>
                {duplicates.map((group) => (
                  <DuplicateCard
                    key={group.group_key}
                    group={group}
                    busyId={busyId}
                    jobInfo={jobInfo}
                    onLink={link}
                    onStartMerge={(apps, allowOrphan) =>
                      setMergeTarget({ apps, allowOrphan, groupKey: group.group_key })
                    }
                  />
                ))}
              </div>
            ) : null}

            {possibleDuplicates.length > 0 ? (
              <div className="review__section">
                <h2 className="review__section-title">Possible duplicates ({possibleDuplicates.length})</h2>
                <p className="review__section-note">
                  Same job, type and authority with near-matching references (e.g. a “B/” prefix or
                  “/A”/“R1” suffix variant). Review each before merging.
                </p>
                {possibleDuplicates.map((group) => (
                  <DuplicateCard
                    key={`pd-${group.applications[0]?.id ?? group.group_key}`}
                    group={group}
                    busyId={busyId}
                    jobInfo={jobInfo}
                    onLink={link}
                    onStartMerge={(apps, allowOrphan) =>
                      setMergeTarget({ apps, allowOrphan, groupKey: group.group_key })
                    }
                  />
                ))}
              </div>
            ) : null}
          </>
        )
      ) : null}

      {mergeTarget ? (
        <MergeDialog
          apps={mergeTarget.apps}
          allowOrphan={mergeTarget.allowOrphan}
          groupKey={mergeTarget.groupKey}
          onClose={() => setMergeTarget(null)}
          onMerged={(message) => {
            setMergeTarget(null);
            showToast(message);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}

/* ─── shared app blocks ────────────────────────────────────────────────── */

function AppIdentity({ app }: { app: ReviewAppSummary }) {
  return (
    <div className="review-app__id">
      <span className="review-app__ref">{app.app_reference || "(no reference)"}</span>
      {app.previous_reference ? <span className="review-app__was">was {app.previous_reference}</span> : null}
      <Pill tone="neutral">{appTypeLabel(app.type)}</Pill>
      {app.match_status ? <Pill tone={matchStatusTone(app.match_status)}>{humanise(app.match_status)}</Pill> : null}
    </div>
  );
}

function AppMeta({ app }: { app: ReviewAppSummary }) {
  const address = [app.site_address, app.site_postcode].filter(Boolean).join(", ");
  const letter = [humanise(app.latest_letter_type), app.latest_letter_subtype ? humanise(app.latest_letter_subtype) : null]
    .filter((s) => s && s !== "—")
    .join(" · ");
  return (
    <div className="review-app__meta">
      <span>{app.local_authority_or_bcb || "—"}</span>
      <span>{address || "—"}</span>
      <span className="review-app__letter">
        Latest letter: {letter || "—"} · {formatDateDMY(app.latest_letter_date)}
      </span>
      {app.date_statutory_deadline > 0 ? (
        <span>Statutory deadline: {formatDateDMY(app.date_statutory_deadline)}</span>
      ) : null}
    </div>
  );
}

/* ─── unmatched card ───────────────────────────────────────────────────── */

function UnmatchedCard({
  app,
  busy,
  jobInfo,
  onLink,
}: {
  app: ReviewUnmatchedApp;
  busy: boolean;
  jobInfo: Record<number, JobInfo>;
  onLink: (jobId: number, jobDesc: string) => void;
}) {
  // Sibling apps share this app's reference (same-ref duplicates). Those already
  // on a job give a "this is a duplicate — link it to that job" shortcut. Dedupe
  // by job id (several siblings can sit on one job).
  const siblingJobIds = useMemo(() => {
    const ids = new Set<number>();
    for (const s of app.suggested_apps) if (s.job_id > 0) ids.add(s.job_id);
    return [...ids];
  }, [app.suggested_apps]);

  return (
    <article className="review-app">
      <AppIdentity app={app} />
      <AppMeta app={app} />
      <div className="review-app__actions">
        <span className="review-app__actions-label">Link to job</span>

        {app.suggested_jobs.map((j) => (
          <div key={`sj-${j.id}`} className="review-link">
            <span className="review-link__job">
              #{j.job_number} {j.job_name || j.full_address || "—"}
            </span>
            <Pill tone={j.confidence === "address_match" ? "success" : "info-soft"}>
              {confidenceLabel(j.confidence)}
            </Pill>
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              disabled={busy}
              onClick={() => onLink(j.id, `job #${j.job_number}`)}
            >
              Link
            </Button>
          </div>
        ))}

        {siblingJobIds.map((jobId) => (
          <div key={`sib-${jobId}`} className="review-link">
            <span className="review-link__job">
              A duplicate of this application is already linked to {jobLabelText(jobInfo, jobId)}
            </span>
            {isAutoJob(jobInfo, jobId) ? <AutoJobPill /> : null}
            <Button
              size="sm"
              variant="secondary"
              loading={busy}
              disabled={busy}
              onClick={() => onLink(jobId, jobToastDesc(jobInfo, jobId))}
            >
              Link here too
            </Button>
          </div>
        ))}

        <JobPicker busy={busy} onPick={(job) => onLink(job.id, `job #${job.job_number}`)} />
      </div>
    </article>
  );
}

/* ─── duplicate group card ─────────────────────────────────────────────── */

function DuplicateCard({
  group,
  busyId,
  jobInfo,
  onLink,
  onStartMerge,
}: {
  group: ReviewDuplicateGroup;
  busyId: number | null;
  jobInfo: Record<number, JobInfo>;
  onLink: (app: ReviewAppSummary, jobId: number, jobDesc: string) => void;
  onStartMerge: (apps: ReviewAppSummary[], allowOrphan: boolean) => void;
}) {
  // The member already on a job anchors a "link the unlinked siblings here" action.
  const anchor = group.applications.find((a) => a.job_id > 0);
  const plan = planMerge(group.applications);
  // The single real (numbered) job in the group, if any — the target to
  // consolidate the duplicates onto so they can be merged. Auto-created shell
  // jobs (job_number 0) don't count, so a group split across "real job + shell"
  // resolves to the real one.
  const realJobIds = [
    ...new Set(
      group.applications.filter((a) => a.job_id > 0 && (jobInfo[a.job_id]?.number ?? 0) > 0).map((a) => a.job_id),
    ),
  ];
  const canonicalJobId = realJobIds.length === 1 ? realJobIds[0] : 0;
  return (
    <article className="review-dupe">
      <header className="review-dupe__head">
        <span className="review-dupe__key">{group.group_key}</span>
        <span className="review-dupe__count">{group.count} records</span>
        <Button
          size="sm"
          variant="secondary"
          className="review-dupe__merge"
          disabled={!plan.ok}
          title={plan.ok ? `Merge these ${group.applications.length} records into one` : plan.reason}
          onClick={plan.ok ? () => onStartMerge(group.applications, plan.allowOrphan) : undefined}
        >
          Merge
        </Button>
      </header>
      <ul className="review-dupe__members">
        {group.applications.map((a) => (
          <li key={a.id} className="review-dupe__member">
            <AppIdentity app={a} />
            {canonicalJobId > 0 && a.job_id === canonicalJobId ? (
              // Already on the canonical real job — nothing to do.
              <span className="review-dupe__joblink-wrap">
                <Link className="review-dupe__joblink" to={`/staff/jobs/${a.job_id}`}>
                  On {jobLabelText(jobInfo, a.job_id)} ↗
                </Link>
              </span>
            ) : canonicalJobId > 0 ? (
              // A real job exists in the group but this record isn't on it →
              // offer to move/link it there so the group can be merged.
              <span className="review-dupe__joblink-wrap">
                {a.job_id > 0 ? (
                  <Link className="review-dupe__joblink review-dupe__joblink--from" to={`/staff/jobs/${a.job_id}`}>
                    On {jobLabelText(jobInfo, a.job_id)} ↗
                  </Link>
                ) : null}
                {a.job_id > 0 && isAutoJob(jobInfo, a.job_id) ? <AutoJobPill /> : null}
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busyId === a.id}
                  disabled={busyId === a.id}
                  onClick={() => onLink(a, canonicalJobId, jobToastDesc(jobInfo, canonicalJobId))}
                >
                  {a.job_id > 0 ? "Move to" : "Link to"} {jobLabelText(jobInfo, canonicalJobId)}
                </Button>
              </span>
            ) : a.job_id > 0 ? (
              <span className="review-dupe__joblink-wrap">
                <Link className="review-dupe__joblink" to={`/staff/jobs/${a.job_id}`}>
                  On {jobLabelText(jobInfo, a.job_id)} ↗
                </Link>
                {isAutoJob(jobInfo, a.job_id) ? <AutoJobPill /> : null}
              </span>
            ) : anchor ? (
              <span className="review-dupe__joblink-wrap">
                <Button
                  size="sm"
                  variant="ghost"
                  loading={busyId === a.id}
                  disabled={busyId === a.id}
                  onClick={() => onLink(a, anchor.job_id, jobToastDesc(jobInfo, anchor.job_id))}
                >
                  Link to {jobLabelText(jobInfo, anchor.job_id)}
                </Button>
                {isAutoJob(jobInfo, anchor.job_id) ? <AutoJobPill /> : null}
              </span>
            ) : (
              <span className="review-dupe__unlinked">Unlinked</span>
            )}
          </li>
        ))}
      </ul>
      {!plan.ok ? (
        <p className="review-dupe__note">
          {plan.reason}
          {canonicalJobId > 0 ? ` Move every record onto ${jobLabelText(jobInfo, canonicalJobId)}, then Merge.` : ""}
        </p>
      ) : null}
    </article>
  );
}

/* ─── merge preview + confirm dialog ───────────────────────────────────── */

function MergeDialog({
  apps,
  allowOrphan,
  groupKey,
  onClose,
  onMerged,
}: {
  apps: ReviewAppSummary[];
  allowOrphan: boolean;
  groupKey: string;
  onClose: () => void;
  onMerged: (message: string) => void;
}) {
  const ids = useMemo(() => apps.map((a) => a.id), [apps]);
  const refById = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of apps) m.set(a.id, refOf(a));
    return m;
  }, [apps]);
  const refFor = (id: number) => refById.get(id) ?? `application ${id}`;

  const [preview, setPreview] = useState<ApplicationMergePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [committing, setCommitting] = useState(false);

  // Dry-run preview on open. ids/allowOrphan are fixed for a given dialog instance.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    applicationsApi
      .previewApplicationMerge(ids, allowOrphan)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not preview the merge.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ids, allowOrphan]);

  // Esc closes (unless a commit is in flight).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !committing) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [committing, onClose]);

  async function confirm() {
    setCommitting(true);
    setError(undefined);
    try {
      const res = await applicationsApi.mergeApplications(ids, allowOrphan);
      const n = res.losers_merged.length;
      onMerged(`Merged ${n} record${n === 1 ? "" : "s"} into ${refFor(res.winner_id)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not merge the applications.");
      setCommitting(false);
    }
  }

  const letters = preview ? preview.repointed.planning_letter + preview.repointed.building_control_letter : 0;

  return (
    <div className="review-modal__overlay" role="dialog" aria-modal="true" onClick={committing ? undefined : onClose}>
      <div className="review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="review-modal__head">
          <h2>Merge duplicates</h2>
          <span className="review-modal__key">{groupKey}</span>
        </div>

        <div className="review-modal__body">
          {loading ? (
            <Spinner label="Previewing merge…" />
          ) : error && !preview ? (
            <p className="leads__error" role="alert">{error}</p>
          ) : preview ? (
            <>
              <p>
                Keep <strong>{refFor(preview.winner_id)}</strong> (application #{preview.winner_id})
                {preview.allow_orphan_loser ? " — the record already on a job" : " — the most recently updated record"} and merge in{" "}
                <strong>{preview.losers_to_merge.length}</strong> duplicate{preview.losers_to_merge.length === 1 ? "" : "s"}:
              </p>
              <ul className="review-modal__losers">
                {preview.losers_to_merge.map((id) => (
                  <li key={id}>
                    {refFor(id)} <span className="review-modal__loser-id">(#{id})</span>
                  </li>
                ))}
              </ul>
              <ul className="review-modal__effects">
                <li>Repoints {letters} letter{letters === 1 ? "" : "s"}, {preview.repointed.fee} fee{preview.repointed.fee === 1 ? "" : "s"} and {preview.repointed.task} task{preview.repointed.task === 1 ? "" : "s"} onto the winner.</li>
                {preview.winner_fields_to_backfill.length > 0 ? (
                  <li>Backfills the winner's {preview.winner_fields_to_backfill.map(humanise).join(", ")}.</li>
                ) : null}
                <li>Closes the merged record{preview.losers_to_merge.length === 1 ? "" : "s"} with a “merged into #{preview.winner_id}” tombstone.</li>
              </ul>
              <p className="review-modal__warn">This rewrites the records and can't be undone from here.</p>
              {error ? <p className="leads__error" role="alert">{error}</p> : null}
            </>
          ) : null}
        </div>

        <div className="review-modal__foot">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={committing}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={confirm}
            loading={committing}
            disabled={loading || !preview}
          >
            {preview ? `Merge ${preview.losers_to_merge.length + 1} records` : "Merge"}
          </Button>
        </div>
      </div>
    </div>
  );
}

